import { Link } from "@/components";
import { UserInfoWrapper } from "@/components/UserInfo/UserInfoWrapper";
import { ProfilePhoto } from "@/containers/Profile/components";
import { routes } from "@/helpers";
import { IUserSearchInfo } from "@/interfaces/User/IUserSearchInfo";
import { useGetConditionalAccessGroupMembersQuery } from "@/services";
import { Card, List, Space, Switch } from "antd";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import { SearchUserInput } from "./SearchUserInput";

interface IProps {
  id: string;
  domain: string;
}

interface IRequest {
  id: string;
  domain: string;
  page: number;
  pageSize: number;
  userId: string;
}

const DEFAULT_PAGE_SIZE = 20;

export const ConditionalAccessGroupMembers = ({ id, domain }: IProps) => {
  const intl = useIntl();
  const [request, setRequest] = useState<IRequest>({
    id,
    domain,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    userId: "",
  });
  const [hideImage, setHideImage] = useState(true);
  const {
    data: groupMembers,
    isFetching,
    isError,
    refetch,
  } = useGetConditionalAccessGroupMembersQuery(request, {
    skip: !id || !domain,
  });

  useEffect(() => {
    refetch();
  }, [request, refetch]);

  useEffect(() => {
    if (!isError) return;

    toast.error(intl.formatMessage({ id: "general.toastSomethingWentWrong" }));
  }, [intl, isError]);

  const handlePageChange = (page: number, pageSize: number) => {
    setRequest((prevState) => ({ ...prevState, page, pageSize }));
  };

  const handleImageChange = (checked: boolean) => {
    setHideImage(checked);
  };

  const handleUserSearch = (selectedUser: string) => {
    setRequest((prevState) => ({
      ...prevState,
      page: 1,
      userId: selectedUser || "",
    }));
  };

  return (
    <Card
      title={intl.formatMessage(
        { id: "conditionalAccess.members" },
        { count: groupMembers?.total }
      )}
      loading={isFetching}
      extra={
        <Space size="large">
          <SearchUserInput onSearch={handleUserSearch} />
          <Switch
            onChange={handleImageChange}
            defaultChecked={true}
            data-test-id={"hide-image-switch"}
            unCheckedChildren={intl.formatMessage({
              id: "conditionalAccess.hideImages",
            })}
            checkedChildren={intl.formatMessage({
              id: "conditionalAccess.showImages",
            })}
          />
        </Space>
      }
    >
      <List
        dataSource={groupMembers?.members}
        grid={{ gutter: 16, xs: 1, sm: 1, md: 2, lg: 2, xl: 3, xxl: 4 }}
        pagination={{
          onChange: handlePageChange,
          total: groupMembers?.total,
          showSizeChanger: true,
          current: request.page,
          defaultPageSize: request.pageSize,
          pageSizeOptions: ["10", "20", "50"],
          hideOnSinglePage: true,
        }}
        renderItem={(groupMember: IGroupMember) => (
          <List.Item>
            <List.Item.Meta
              avatar={
                !hideImage && (
                  <ProfilePhoto userId={groupMember.user.id} size={64} />
                )
              }
              title={
                <Link to={routes.admin.users.get(groupMember.user.id)}>
                  <UserInfoWrapper user={groupMember.user}>
                    {groupMember.user.displayName}
                  </UserInfoWrapper>
                </Link>
              }
              description={groupMember.groups.map((x, index) => (
                <Link to={routes.admin.conditionalAccess.get(x.id)} key={x.id}>
                  <span>{x.name}</span>
                  {index !== groupMember.groups.length - 1 && <span>, </span>}
                </Link>
              ))}
            />
          </List.Item>
        )}
      />
    </Card>
  );
};

interface IGroupMember {
  groups: Array<{ id: string; name: string }>;
  user: IUserSearchInfo;
}
